import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import LeadQuestionnaire from '../LeadQuestionnaire';
import { MaskedLines } from '../Reveal';
import { coach } from '../../config/coach';

const HeroSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '12%']);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.06, 1.14]);

  return (
    <section id="hero" ref={ref} className="relative min-h-[100svh] overflow-hidden" data-testid="hero-section">
      {/* Parallax background — face framed in upper area, revealed above the form on mobile */}
      <motion.div className="absolute inset-0 will-change-transform" style={{ y: imgY, scale: imgScale }}>
        <img
          src={coach.images.hero}
          alt={`${coach.fullName} — physique coach`}
          className="w-full h-full object-cover object-[44%_35%] sm:object-[42%_26%] img-grade"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/15 via-black/35 to-black sm:from-black/55 sm:via-black/45 sm:to-black/85" />
      <div className="hidden lg:block absolute inset-0 bg-gradient-to-r from-black/70 via-black/20 to-transparent" />

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-8 min-h-[100svh] flex flex-col">
        {/* Badge */}
        <div className="pt-20 sm:pt-28 flex justify-center lg:justify-start">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
            className="inline-flex items-center gap-2 bg-white/5 backdrop-blur-md border border-white/15 rounded-full px-4 py-2"
            data-testid="hero-badge"
          >
            <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
            <span className="text-xs sm:text-sm font-medium text-white/90 uppercase tracking-widest">{coach.hero.badge}</span>
          </motion.div>
        </div>

        {/* Mobile image-reveal band — keeps Rasul's face & physique visible above the form */}
        <div className="h-[48vh] lg:hidden" />

        {/* Content: form-centric on mobile, headline + form on desktop */}
        <div className="flex-1 grid lg:grid-cols-2 gap-10 lg:items-center pb-8 sm:pb-10">
          {/* Left — kinetic headline (desktop only) */}
          <div className="hidden lg:flex flex-col justify-center">
            <MaskedLines
              lines={coach.hero.headline}
              delay={0.4}
              className="font-display font-bold uppercase text-white tracking-[-0.02em] leading-[0.82] text-7xl xl:text-8xl"
            />
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1 }}
              className="mt-7 max-w-md text-white/70 text-lg leading-relaxed"
            >
              {coach.hero.sub}
            </motion.p>
          </div>

          {/* Right — questionnaire (the primary conversion point) */}
          <div id="apply" className="w-full flex justify-center lg:justify-end scroll-mt-24" data-testid="apply-anchor">
            <div className="w-full max-w-md">
              <LeadQuestionnaire />
              <p className="text-white/50 text-xs sm:text-sm text-center mt-3 sm:mt-4 leading-relaxed">
                No spam. No pressure.<br className="sm:hidden" /> If we're not a fit, I'll tell you.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
